import React, { useState } from 'react';
import Fireworks from 'react-canvas-confetti/dist/presets/fireworks';
import { useMetadata } from '../../context/MetadataContext'; 
import { getValue } from '../../utils/getValue';
import FormattedTime from '../../utils/FormattedTime';
import { useGameState } from '../../context/GameStateContext';
import { useGlobalError } from '../../context/GlobalErrorContext';
import { useSoloChallenge } from '../../hooks/useSoloChallenge';
import ShareModal from '../ShareModal';
import PuzzleLeaderboard from '../PuzzleLeaderboard';
import { Swords, Search, Home, List } from 'lucide-react';  // Challenge (duel), Home, Search, List

const SoloResults = () => {
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [leaderboardModalOpen, setLeaderboardModalOpen] = useState(false);
  const [challengeUrl, setChallengeUrl] = useState('');
  const { getText } = useMetadata();
  const { gameData, rejectPuzzle, findPuzzle } = useGameState();
  const { triggerError } = useGlobalError();
  const { isLoading, createChallenge } = useSoloChallenge();


  

  // Validation check after all hooks
  if (!gameData?.state) {
    triggerError({ type: 'non-critical', message: 'SoloResults component rendered without puzzle data. This indicates a routing/status determination error.' });
    return null;
  }

  const onCloseResults = () => {
    rejectPuzzle();
  };

  const onFindAgain = () => {
    findPuzzle();
  }

  const onChallenge = async () => {
    const challengeCode = await createChallenge();

    if (challengeCode) {
      // Construct the challenge URL for the frontend application using query parameters
      // This creates a URL that will open the app with the challenge code as a query parameter
      const challengeUrl = `${window.location.origin}?challenge_code=${challengeCode}`;
      setChallengeUrl(challengeUrl);
      setShareModalOpen(true);
    }
  }


  return (
    <div className="dataContainer">
      
      <div className="containerHeader">
      <Fireworks autorun={{ speed: 1, duration: 2000 }} />
      </div>
      
      <div className="dataContent">
        <div className="contentGroup">    
          <div className="groupHeader">
            <h3>{getText('common_text.your_results')}</h3>
          </div>

          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.total_time')}:</span>
            <span className="dataValue"><FormattedTime seconds={getValue(gameData,'state.total_time',0)} /></span>
          </div>
          <div className="subDataRow">
            <span className="subDataLabel">{getText('common_text.clock_time')}:</span>
            <span className="subDataValue"><FormattedTime seconds={getValue(gameData,'state.clock_time',0)} /></span>
          </div>
          
          <div className="subDataRow">
            <span className="subDataLabel">
              {getText('common_text.penalty_time')} ({getText('common_text.hints').toLowerCase()} {getValue(gameData,'state.hint_count')}):
           </span>
            <span className="subDataValue">
              <FormattedTime seconds={getValue(gameData, 'state.penalty_time')} />
            </span>
          </div>

          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.moves')}:</span> 
            <span className="dataValue">{getValue(gameData,'state.move_count')}</span>
          </div>

        </div>
        
        <div className="contentGroup">
          <div className="groupHeader">
            <h3>{getText('common_text.your_ranking')}</h3>
          </div>
          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.global_rank')}</span>
            <span className="dataValue">{getValue(gameData, 'stats.global_rank', 0)}
              &nbsp;/&nbsp;{getValue(gameData, 'stats.global_solves', 0)}
            </span>
          </div>
          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.country_rank')}</span>
            <span className="dataValue">{getValue(gameData, 'stats.country_rank', 0)}
              &nbsp;/&nbsp;{getValue(gameData, 'stats.country_solves', 0)}
            </span>
          </div>
          {getValue(gameData, 'stats.age_rank', 0) > 0 && (
          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.age_rank')}</span>
            <span className="dataValue">{getValue(gameData, 'stats.age_rank', 0)}
              &nbsp;/&nbsp;{getValue(gameData, 'stats.age_solves', 0)}
            </span>
          </div>
          )}
          {getValue(gameData, 'stats.gender_rank', 0) > 0 && (
          <div className="dataRow">
            <span className="dataLabel">{getText('common_text.gender_rank')}</span>
            <span className="dataValue">{getValue(gameData, 'stats.gender_rank', 0)}
              &nbsp;/&nbsp;{getValue(gameData, 'stats.gender_solves', 0)}
            </span>
          </div>
          )}
        </div>
      </div>
      
      <div className="actionButtonContainer">
        <button 
          className="iconButton secondaryButton"
          onClick={onCloseResults}
          title={getText('common_text.back')}
          aria-label={getText('common_text.back')}
        >
          <Home className="buttonIcon" />
        </button>
        <button 
          className="iconButton secondaryButton"
          onClick={() => setLeaderboardModalOpen(true)}
          title={getText('common_text.leaderboard')}
          aria-label={getText('common_text.leaderboard')}
        >
          <List className="buttonIcon" />
        </button>
        <button 
          className="iconButton secondaryButton"
          onClick={onChallenge} 
          disabled={isLoading}
          title={isLoading ? getText('common_text.loading') : getText('actions.action_challenge')}
          aria-label={isLoading ? getText('common_text.loading') : getText('actions.action_challenge')}
        >
          <Swords className="buttonIcon" />
        </button>
        <button 
          className="iconButton primaryButton"
          onClick={onFindAgain}
          title={getText('common_text.play_again')}
          aria-label={getText('common_text.play_again')}
        >
          <Search className="buttonIcon" />
        </button>
      </div>
      
      {/* Challenge Modal */}
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        shareUrl={challengeUrl}
        modalType="challenge"
      />
      
      {/* Puzzle Leaderboard Modal */}
      <PuzzleLeaderboard
        isOpen={leaderboardModalOpen}
        onClose={() => setLeaderboardModalOpen(false)}
      />
    </div>
  );
};

export default SoloResults;
